import { useEffect } from 'react'
import { Play, Pause, SkipBack, SkipForward, RotateCcw } from 'lucide-react'
import { useGameStore } from '@/stores/game.store'

interface StepControlsProps {
  onComplete: () => void
}

export function StepControls({ onComplete }: StepControlsProps) {
  const {
    status,
    stepIndex,
    steps,
    animationSpeedMs,
    incrementStep,
    setStepIndex,
    startRace,
    pauseGame,
    resumeGame,
    finishRace,
    setAnimationSpeed,
    resetGame,
  } = useGameStore()

  const totalSteps = steps.length
  const isAtEnd = stepIndex >= totalSteps - 1

  // Auto-advance when playing
  useEffect(() => {
    if (status !== 'playing') return
    if (isAtEnd) {
      finishRace()
      onComplete()
      return
    }
    const id = setInterval(incrementStep, animationSpeedMs)
    return () => clearInterval(id)
  }, [status, isAtEnd, animationSpeedMs, incrementStep, finishRace, onComplete])

  const handlePlayPause = () => {
    if (status === 'idle') {
      startRace()
    } else if (status === 'playing') {
      pauseGame()
    } else if (status === 'paused') {
      resumeGame()
    }
  }

  const speedOptions = [
    { label: '0.5×', ms: 800 },
    { label: '1×', ms: 400 },
    { label: '2×', ms: 200 },
    { label: '4×', ms: 100 },
    { label: '10×', ms: 40 },
  ]

  return (
    <div className="bg-arcade-surface border-t border-arcade-border px-6 py-4">
      <div className="flex items-center gap-4">
        {/* Step back */}
        <button
          onClick={() => setStepIndex(stepIndex - 1)}
          disabled={stepIndex === 0 || status === 'playing'}
          className="p-2 rounded-lg text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Step back"
        >
          <SkipBack size={18} />
        </button>

        {/* Play / Pause */}
        <button
          onClick={handlePlayPause}
          disabled={status === 'completed' || status === 'failed'}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-arcade-accent hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
          aria-label={status === 'playing' ? 'Pause' : 'Play'}
        >
          {status === 'playing' ? <Pause size={18} /> : <Play size={18} />}
        </button>

        {/* Step forward */}
        <button
          onClick={() => setStepIndex(stepIndex + 1)}
          disabled={isAtEnd || status === 'playing'}
          className="p-2 rounded-lg text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Step forward"
        >
          <SkipForward size={18} />
        </button>

        {/* Reset */}
        <button
          onClick={resetGame}
          className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors ml-1"
          aria-label="Reset"
        >
          <RotateCcw size={16} />
        </button>

        {/* Speed selector */}
        <div className="ml-auto flex items-center gap-1">
          {speedOptions.map((opt) => (
            <button
              key={opt.ms}
              onClick={() => setAnimationSpeed(opt.ms)}
              className={`px-2.5 py-1 rounded text-xs font-mono transition-colors ${
                animationSpeedMs === opt.ms
                  ? 'bg-arcade-accent text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      {totalSteps > 0 && (
        <div className="mt-3 h-1 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-arcade-accent transition-all duration-200"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
      )}
    </div>
  )
}
